import React, { useEffect, useMemo, useRef } from "react";
import { makeStyles } from "tss-react/mui";
import { v4 as uuidv4 } from "uuid";
import { useSearchParams } from "react-router-dom";
import { WAYPOINT_COLOR } from "../../../interfaces/robot/rooms/waypoints";
import { LANDMARK_COLOR, EVACUATION_PLACE_COLOR, LandmarkType } from "../../../interfaces/robot/rooms/landmark";
import { GATE_COLOR } from "../../../interfaces/robot/rooms/gate";
import IdTypePoint from "../../../interfaces/robot/rooms/id-type-point";
import ZonePointType, { SimpleZonePointType } from "../../../enums/robot/rooms/zone-point-type";
import RoomData from "../../../interfaces/robot/rooms/room-data";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { getSvgPointFromMouseClick } from "../../../utils/map/svg-helper";
import { mapScaleToValue } from "../../../utils/map/grid-scale";
import Point, {
  getDistanceBetweenTwoPoints,
  getRosPointFromSvgCoordinates,
  getSvgCoordinatesFromRosPoint,
  setPointInRoom,
} from "../../../interfaces/robot/rooms/point";
import { zonePointsToSvgString } from "../../../utils/map/zone-helper";
import { selectCurrentTeaching } from "../../../store/room/teaching/teaching-selectors";
import ZoneControllerMouseState, { isAddingState } from "../../../enums/robot/zone-planner/zone-controller-mouse-state";
import ZonePlannerModeState from "../../../enums/robot/zone-planner/zone-planner-mode-state";
import {
  selectZonePlannerModeState,
  selectZonePlannerMouseState,
  selectAllZoneIds,
  selectHiddenZoneIds,
  selectSelectedZoneInstance,
  selectTeachAndRepeatPath,
  selectChargingStationDragData,
  selectHiddenGateIds,
  selectSelectedZone,
  selectCurrentTeachingAutoZonePath,
  selectSelectedLandmark,
  selectSelectedWaypointNode,
} from "../../../store/room/zone-planner/zone-planner-selectors";
import { starPoints, treePoints } from "../../../interfaces/robot/zone-planner/christmas-points";
import ZonePlannerMapZone from "./ZonePlannerMapZone";
import {
  selectDistanceUnit,
  selectIsAdvancedModeEnabled,
  selectIsReadOnly,
} from "../../../store/app-configurations/app-configurations-selectors";
import {
  selectRepeatPathsData,
  selectGatesData,
  selectLandmarksData,
  selectRoomCleaningZones,
  selectRoomRedZones,
  selectRoomRuleZones,
  selectSelectedRoomGates,
  selectSelectedRoomLimitZone,
  selectWaypointsData,
  selectRedZonesData,
  selectCleaningZonesData,
} from "../../../store/room/room-selectors";
import WaypointsMap from "./WayPointsMap/WayPointsMap";
import { isCleaningZone, isTeachAndRepeatZone } from "../../../interfaces/robot/rooms/cleaning-zone";
import Landmarks from "./Landmarks";
import ChargingStation from "./ChargingStation";
import GatePosts from "./GatePosts";
import { MoveStationSaveStatus } from "../../../interfaces/robot/rooms/hub-messages/receive-move-station-command-message";
import { calculatePolygonArea, getCursorPointFromEvent } from "../../../utils/map/zone-helper";
import { MapHistoryAction, MapHistoryType } from "../../../interfaces/robot/zone-planner/map-history";
import TeachingCommandMessage, {
  TeachingCommand,
} from "../../../interfaces/robot/rooms/hub-messages/teaching-command-message";
import MessageType from "../../../enums/robot/rsw/message-types";
import TeachAndRepeatZoneGeneratingPathState from "./SelectedControls/TeachAndRepeatZoneGeneratingPathState";
import { FilterKeys } from "../../../enums/query-params/filters";
import SphereRobotTabs from "../../../enums/query-params/sphere-robot-tabs";
import { PathPoint } from "../../../interfaces/robot/rooms/teach-and-repeat-zone-path";
import TeachingWizardStep from "../../../interfaces/robot/wizards/teaching/teaching-wizard-step";
import { START_POINT_OFFSET, validateRedZoneStationOverlap } from "../../../validation/zone-validation";
import { getDefaultChargingStationMove } from "../../../interfaces/robot/rooms/station";
import KRobotSvg from "../../CleaningMap/KRobotSvg";
import { getMapCoordinatesFromPoint } from "../../../utils/map";
import { selectCurrentRobotState } from "../../../store/status/status-selectors";
import RobotVersion, { getRobotWidthFromVersion } from "../../../interfaces/cleaning-map/robot-version";
import {
  selectNavTasksData,
  selectRobotModel,
} from "../../../store/robot-configurations/robot-configurations-selectors";
import { isRuleZone } from "../../../interfaces/robot/rooms/rule-zone";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";

const useStyles = makeStyles()(() => ({
  overlayDrawingSvg: {
    position: "absolute",
    top: 0,
    left: 0,
  },
  gridStyle: {
    position: "absolute",
    pointerEvents: "none",
  },
}));

const MIN_WIDTH_THRESHOLD = 0.5;
const MIN_AREA_THRESHOLD = 0.125;

const newCleaningZoneCursorSvg =
  `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 30 30">` +
  `<circle cx="15" cy="15" r="5" strokeWidth="1" fill="green"/>` +
  `<circle cx="15" cy="15" r="15" strokeWidth="1" opacity="0.5" fill="rgb(144, 238, 144)"/>` +
  `</svg>') 10 10, auto`; // "10 10" = margin so that middle of svg at cursor tip, has to be half ot svg height/width

const newTeachAndRepeatZoneCursorSvg =
  `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 30 30">` +
  `<circle cx="15" cy="15" r="5" strokeWidth="1" fill="blue"/>` +
  `<circle cx="15" cy="15" r="15" strokeWidth="1" opacity="0.5" fill="rgb(17, 143, 243)"/>` +
  `</svg>') 10 10, auto`; // "10 10" = margin so that middle of svg at cursor tip, has to be half ot svg height/width

const newRedZoneCursorSvg =
  `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 30 30">` +
  `<circle cx="15" cy="15" r="5" strokeWidth="1" fill="red"/>` +
  `<circle cx="15" cy="15" r="15" strokeWidth="1" opacity="0.5" fill="rgba(255, 127, 127)"/>` +
  `</svg>') 10 10, auto`; // "10 10" = margin so that middle of svg at cursor tip, has to be half ot svg height/width

const newRuleZoneCursorSvg =
  `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 30 30">` +
  `<circle cx="15" cy="15" r="5" strokeWidth="1" fill="purple"/>` +
  `<circle cx="15" cy="15" r="15" strokeWidth="1" opacity="0.5" fill="rgb(221, 160, 221)"/>` +
  `</svg>') 10 10, auto`; // "10 10" = margin so that middle of svg at cursor tip, has to be half ot svg height/width

const newWayPointCursorSvg =
  `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 30 30">` +
  `<circle cx="15" cy="15" r="5" strokeWidth="1" fill="${WAYPOINT_COLOR}"/>` +
  `<circle cx="15" cy="15" r="15" strokeWidth="1" opacity="0.5" fill="${WAYPOINT_COLOR}"/>` +
  `</svg>') 10 10, auto`; // "10 10" = margin so that middle of svg at cursor tip, has to be half ot svg height/width

const newLandmarkCursorSvg =
  `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 30 30">` +
  `<circle cx="15" cy="15" r="5" strokeWidth="1" fill="${LANDMARK_COLOR}"/>` +
  `<circle cx="15" cy="15" r="15" strokeWidth="1" opacity="0.5" fill="${LANDMARK_COLOR}"/>` +
  `</svg>') 10 10, auto`; // "10 10" = margin so that middle of svg at cursor tip, has to be half ot svg height/width

const newEvacuationPlaceCursorSvg =
  `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 30 30">` +
  `<circle cx="15" cy="15" r="5" strokeWidth="1" fill="${EVACUATION_PLACE_COLOR}"/>` +
  `<circle cx="15" cy="15" r="15" strokeWidth="1" opacity="0.5" fill="${EVACUATION_PLACE_COLOR}"/>` +
  `</svg>') 10 10, auto`; // "10 10" = margin so that middle of svg at cursor tip, has to be half ot svg height/width

export const draggingCursor = "grabbing";
export const grabCursor = "grab";
const copyCursor = "copy";

export const deleteCursorSvg =
  `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 30 30">` +
  `<circle cx="15" cy="15" r="5" strokeWidth="1" fill="red"/>` +
  `<circle cx="15" cy="15" r="15" strokeWidth="1" opacity="0.5" fill="rgba(0, 0, 0, 0.5)"/>` +
  `</svg>') 10 10, auto`; // "10 10" = margin so that middle of svg at cursor tip, has to be half ot svg height/width

const newGateCursorSvg =
  `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 30 30">` +
  `<circle cx="15" cy="15" r="5" strokeWidth="1" fill="${GATE_COLOR}"/>` +
  `<circle cx="15" cy="15" r="15" strokeWidth="1" opacity="0.5" fill="${GATE_COLOR}"/>` +
  `</svg>') 10 10, auto`; // "10 10" = margin so that middle of svg at cursor tip, has to be half ot svg height/width

const mapOriginPoint: IdTypePoint = {
  id: "mapOrigin",
  coordinateX: 0.0,
  coordinateY: 0.0,
  type: ZonePointType.MapOrigin,
};

const ZonePlannerMapZones: React.FC<{
  roomData: RoomData;
  zoomScale: number;
  isWayPointMapViewable?: boolean;
  scheduleLandmarkSelectionProps?: {
    selectedLandmarkId: string;
    onLandmarkClickOverride: (landmarkId: string) => void;
  };
  hoverZoneId?: string | null;
  hoverGateId?: string | null;
  isGridVisible?: boolean;
  isChristmasMode?: boolean;
  hidePaths?: boolean;
  missionView?: { hoveredIds: string[] };
}> = ({
  roomData,
  zoomScale,
  isWayPointMapViewable = false,
  scheduleLandmarkSelectionProps,
  hoverZoneId = null,
  hoverGateId = null,
  isGridVisible = false,
  isChristmasMode = false,
  hidePaths = false,
  missionView,
}) => {
  const { classes } = useStyles();

  const dispatch = useSharedDispatch();

  const {
    updateSelectedZonePointCoordinates,
    setSelectedLandmark,
    setZonePlannerMode,
    setZonePlannerMouseState,
    addSelectedZonePoint,
    setSelectedZone,
    setSelectedGatePoint,
    updateSelectedZoneCoordinatesOnDrag,
    addLandmark,
    addWayPoint,
    updateWayPointMousePosition,
    postNewCleaningZone,
    postNewRedZone,
    postNewRuleZone,
    postNewLandmark,
    patchCleaningZone,
    patchRedZone,
    patchRuleZone,
    dragLandmark,
    patchLandmark,
    dragWayPoint,
    upsertWaypointSet,
    setSelectedWayPoint,
    addUndoableAction,
    sendRobotWebsocketMessage,
    setIsGeneratingTeachPath,
    updateSelectedTeachAndRepeatPathPointCoordinates,
    patchStationDrag,
    patchSelectedGate,
    createNewGate,
    postNewGate,
    patchGateById,
    toggleWaypointsEnabledAutomatically,
    patchNavTasks,
  } = useSharedActions();

  const [filterParams] = useSearchParams();
  const currentTab = filterParams.get(FilterKeys.Tab);

  const zonePlannerMouseState = useSharedSelector((state) => selectZonePlannerMouseState(state));
  const zonePlannerModeState = useSharedSelector((state) => selectZonePlannerModeState(state));
  const currentTeaching = useSharedSelector((state) => selectCurrentTeaching(state));
  const isReadOnly = useSharedSelector((state) => selectIsReadOnly(state));
  const allZoneIds = useSharedSelector((state) => selectAllZoneIds(state));
  const hiddenZoneIds = useSharedSelector((state) => selectHiddenZoneIds(state));
  const hiddenGateIds = useSharedSelector((state) => selectHiddenGateIds(state));
  const cleaningZones = useSharedSelector((state) => selectRoomCleaningZones(state))(roomData.id);
  const cleaningZonesData = useSharedSelector((state) => selectCleaningZonesData(state));
  const redZones = useSharedSelector((state) => selectRoomRedZones(state))(roomData.id);
  const redZonesData = useSharedSelector((state) => selectRedZonesData(state));
  const ruleZones = useSharedSelector((state) => selectRoomRuleZones(state))(roomData.id);
  const selectedZoneInstance = useSharedSelector((state) => selectSelectedZoneInstance(state));
  const chargingStationDragData = useSharedSelector((state) => selectChargingStationDragData(state));
  const selectedWaypointNode = useSharedSelector((state) => selectSelectedWaypointNode(state));
  const waypointsData = useSharedSelector((state) => selectWaypointsData(state));
  const gates = useSharedSelector((state) => selectSelectedRoomGates(state));
  const gatesData = useSharedSelector((state) => selectGatesData(state));
  const roomLimitZone = useSharedSelector((state) => selectSelectedRoomLimitZone(state));
  const landmarksData = useSharedSelector((state) => selectLandmarksData(state));
  const isAdvancedModeEnabled = useSharedSelector((state) => selectIsAdvancedModeEnabled(state));
  const selectedZone = useSharedSelector((state) => selectSelectedZone(state));
  const selectedTrPath = useSharedSelector((state) => selectCurrentTeachingAutoZonePath(state));
  const selectedTrPathInstance = useSharedSelector((state) => selectTeachAndRepeatPath(state));
  const selectedLandmark = useSharedSelector((state) => selectSelectedLandmark(state));
  const distanceUnit = useSharedSelector((state) => selectDistanceUnit(state));
  const currentRobotState = useSharedSelector((state) => selectCurrentRobotState(state));
  const robotVersion = useSharedSelector((state) => selectRobotModel(state));
  const navTasks = useSharedSelector((state) => selectNavTasksData(state));

  const svgRef = useRef<SVGSVGElement>(null);
  const isDraggingRef = useRef(false);
  const pointerIdRef = useRef<number | null>(null);
  const draggingStartRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (zonePlannerMouseState === ZoneControllerMouseState.CopyZone && selectedZoneInstance != null) {
      if (isTeachAndRepeatZone(selectedZoneInstance)) {
        if (selectedTrPathInstance == null) return;
        dispatch(
          postNewCleaningZone({ cleaningZone: selectedZoneInstance, teachAndRepeatPath: selectedTrPathInstance }),
        );
      } else if (isCleaningZone(selectedZoneInstance)) {
        dispatch(postNewCleaningZone({ cleaningZone: selectedZoneInstance }));
      } else if (isRuleZone(selectedZoneInstance)) {
        dispatch(postNewRuleZone(selectedZoneInstance));
      } else {
        dispatch(postNewRedZone(selectedZoneInstance));
      }
      dispatch(
        addUndoableAction({
          action: MapHistoryAction.Created,
          type: isCleaningZone(selectedZoneInstance) ? MapHistoryType.CleaningZone : MapHistoryType.RedZone,
          changedData: selectedZoneInstance,
          additionalChangedData: selectedTrPathInstance ?? undefined,
        }),
      );
      dispatch(setZonePlannerMouseState(ZoneControllerMouseState.DragZone));
    }
  }, [
    zonePlannerMouseState,
    selectedZoneInstance,
    selectedTrPathInstance,
    dispatch,
    postNewRedZone,
    setZonePlannerMouseState,
  ]);

  useEffect(() => {
    if (
      currentTab !== SphereRobotTabs.MapPlanner &&
      currentTeaching.currentStep !== null &&
      currentTeaching.currentStep !== TeachingWizardStep.EditZoneStep
    ) {
      // Based of wizard step
      dispatch(setZonePlannerMouseState(ZoneControllerMouseState.Default));
      dispatch(setZonePlannerMode(ZonePlannerModeState.Default));
      dispatch(setSelectedZone(null));
    }
  }, []);

  const robotRadius = useMemo(() => {
    return Math.round(
      getRobotWidthFromVersion(robotVersion ?? RobotVersion.K900) / ((roomData?.mapInformation.resolution ?? 1) * 2),
    );
  }, [roomData?.mapInformation.resolution, robotVersion]);

  const dragGateHandler = (event: React.PointerEvent) => {
    const svgPoint = getSvgPointFromMouseClick(event, svgRef, zoomScale);
    const pointCoordinates = getRosPointFromSvgCoordinates(svgPoint!.x, svgPoint!.y, roomData);
    if (pointCoordinates == null || gatesData.selectedId == null || gatesData.selectedPointId == null) {
      return;
    }
    dispatch(patchSelectedGate({ postCoordinates: setPointInRoom(pointCoordinates, roomData) }));
  };

  const svgMouseTouchDownHandler = (event: React.PointerEvent) => {
    const cursorPoint = getCursorPointFromEvent(event.nativeEvent);
    const notClickedOnPolygon =
      document
        .elementsFromPoint(cursorPoint.coordinateX, cursorPoint.coordinateY)
        .find((e) => e.tagName === "polygon") === undefined;
    const notClickedOnCircle =
      document
        .elementsFromPoint(cursorPoint.coordinateX, cursorPoint.coordinateY)
        .find((e) => e.tagName === "circle") === undefined;

    const svgPoint = getSvgPointFromMouseClick(event, svgRef, zoomScale);
    const newPoint = getRosPointFromSvgCoordinates(svgPoint!.x, svgPoint!.y, roomData);
    if (newPoint == null) {
      return;
    }

    if (zonePlannerMouseState === ZoneControllerMouseState.AddWayPoint && event.type === "pointerdown") {
      if (notClickedOnCircle) {
        // if we clicked on a circle we clicked on another waypoint -> create connection not new waypoint
        dispatch(
          addWayPoint({
            point: { ...setPointInRoom(newPoint, roomData), coordinateTheta: 0 },
            id: uuidv4(),
            gateId: "",
            room: roomData.id,
          }),
        );
      }
      return;
    }
    if (zonePlannerMouseState === ZoneControllerMouseState.AddEvacuationPlace && event.type === "pointerdown") {
      dispatch(addLandmark({ point: { ...setPointInRoom(newPoint, roomData) }, type: LandmarkType.Evacuation }));
      return;
    }
    if (zonePlannerMouseState === ZoneControllerMouseState.AddLandmarkPoint && event.type === "pointerdown") {
      dispatch(addLandmark({ point: { ...setPointInRoom(newPoint, roomData) }, type: LandmarkType.Maintenance }));
      return;
    }
    if (zonePlannerMouseState === ZoneControllerMouseState.AddGate && event.type === "pointerdown") {
      dispatch(createNewGate(setPointInRoom(newPoint, roomData)));
      return;
    }

    if (isAddingState(zonePlannerMouseState)) {
      const newPoints: { points: IdTypePoint[]; repeat: number } = { points: [], repeat: 1 };
      if (zonePlannerMouseState === ZoneControllerMouseState.AddRedZoneLine) {
        newPoints.points.push({
          id: uuidv4(),
          type: ZonePointType.RedZoneCorner,
          ...setPointInRoom(
            {
              coordinateX: newPoint.coordinateX,
              coordinateY: newPoint.coordinateY,
            } as Point,
            roomData,
          ),
        });
        newPoints.repeat = 2;
      } else if (isChristmasMode) {
        if (zonePlannerMouseState === ZoneControllerMouseState.AddRedZone) {
          starPoints.forEach((p) =>
            newPoints.points.push({
              ...setPointInRoom(
                {
                  coordinateX: newPoint.coordinateX + p.coordinateX,
                  coordinateY: newPoint.coordinateY + p.coordinateY,
                },
                roomData,
              ),
              id: uuidv4(),
              type: ZonePointType.RedZoneCorner,
            }),
          );
        } else {
          treePoints.forEach((p) =>
            newPoints.points.push({
              ...setPointInRoom(
                {
                  coordinateX: newPoint.coordinateX + p.coordinateX,
                  coordinateY: newPoint.coordinateY + p.coordinateY,
                },
                roomData,
              ),
              id: uuidv4(),
              type: ZonePointType.CleaningZoneCorner,
            }),
          );
        }
      } else {
        newPoints.points.push({
          ...setPointInRoom(
            {
              coordinateX: newPoint.coordinateX,
              coordinateY: newPoint.coordinateY,
            } as Point,
            roomData,
          ),
          id: uuidv4(),
          type:
            zonePlannerMouseState === ZoneControllerMouseState.AddRedZone
              ? ZonePointType.RedZoneCorner
              : zonePlannerMouseState === ZoneControllerMouseState.AddTeachAndRepeatZone
                ? ZonePointType.TeachAndRepeatZoneCorner
                : zonePlannerMouseState === ZoneControllerMouseState.AddRuleZone
                  ? ZonePointType.RuleZoneCorner
                  : ZonePointType.CleaningZoneCorner,
        });
        newPoints.repeat = 4;
      }

      let finalPointsList: IdTypePoint[] = [];
      for (let i = 0; i < newPoints.repeat; i++) {
        newPoints.points.forEach((np) => {
          const newId = uuidv4();
          finalPointsList.push({ ...np, id: newId });
          dispatch(
            addSelectedZonePoint({
              point: np,
              type: SimpleZonePointType.CornerPoint,
              insertAtIndex: null,
              unselectPoint: true,
              id: newId,
            }),
          );
        });
      }

      dispatch(
        addSelectedZonePoint({
          point: {
            coordinateX: newPoint.coordinateX,
            coordinateY: newPoint.coordinateY,
          } as Point,
          type: SimpleZonePointType.CleaningZoneStartPosition,
          insertAtIndex: null,
          id: uuidv4(),
        }),
      );
      return;
    }

    // Deselect zone if clicked on empty map
    if (notClickedOnPolygon && notClickedOnCircle) {
      dispatch(setSelectedZone(null));
      dispatch(setSelectedLandmark(null));
      dispatch(setSelectedGatePoint({ pointId: null }));
      dispatch(setSelectedWayPoint(null));
      dispatch(setZonePlannerMouseState(ZoneControllerMouseState.Default));
    }
  };

  const generatePath = (zonePoints: number[][], zoneStartPosition: number[]) => {
    if (selectedZoneInstance == null) {
      return;
    }

    const generatePathMsg: TeachingCommandMessage = {
      roomId: roomData.id,
      zoneId: selectedZoneInstance.id,
      zoneName: selectedZoneInstance.name,
      zonePoints: zonePoints,
      zoneStartPosition: zoneStartPosition,
      type: TeachingCommand.AutoGeneratePath,
    };

    dispatch(setIsGeneratingTeachPath());
    dispatch(sendRobotWebsocketMessage({ message: generatePathMsg, type: MessageType.Teaching }));
  };

  const dispatchUpdatePoint = (updatePoint: { id: string; point: Point }) => {
    dispatch(
      updateSelectedZonePointCoordinates({
        pointId: updatePoint.id,
        point: updatePoint.point,
      }),
    );
  };

  const dragCreateMoveHandler = (event: React.PointerEvent) => {
    const svgPoint = getSvgPointFromMouseClick(event, svgRef, zoomScale);
    const newPoint = getRosPointFromSvgCoordinates(svgPoint!.x, svgPoint!.y, roomData);

    if (selectedZoneInstance === null || newPoint === null) {
      return;
    }

    const updatedPoints: { id: string; point: Point }[] = [];
    if (zonePlannerMouseState === ZoneControllerMouseState.AddRedZoneLine && selectedZoneInstance.points.length === 2) {
      updatedPoints.push({
        id: selectedZoneInstance.points[1].point.id,
        point: setPointInRoom(
          {
            coordinateX: newPoint.coordinateX,
            coordinateY: newPoint.coordinateY,
          } as Point,
          roomData,
        ),
      });
    } else {
      const setPoint = selectedZoneInstance.points[0].point;
      updatedPoints.push({
        id: selectedZoneInstance.points[0].point.id,
        point: setPointInRoom(
          {
            coordinateX: setPoint.coordinateX,
            coordinateY: setPoint.coordinateY,
          } as Point,
          roomData,
        ),
      });
      updatedPoints.push({
        id: selectedZoneInstance.points[1].point.id,
        point: setPointInRoom(
          {
            coordinateX: newPoint.coordinateX,
            coordinateY: setPoint.coordinateY,
          } as Point,
          roomData,
        ),
      });
      updatedPoints.push({
        id: selectedZoneInstance.points[2].point.id,
        point: setPointInRoom(
          {
            coordinateX: newPoint.coordinateX,
            coordinateY: newPoint.coordinateY,
          } as Point,
          roomData,
        ),
      });
      updatedPoints.push({
        id: selectedZoneInstance.points[3].point.id,
        point: setPointInRoom(
          {
            coordinateX: setPoint.coordinateX,
            coordinateY: newPoint.coordinateY,
          } as Point,
          roomData,
        ),
      });
    }
    updatedPoints.forEach((np) => {
      dispatchUpdatePoint(np);
    });
    if (isCleaningZone(selectedZoneInstance)) {
      dispatch(
        updateSelectedZonePointCoordinates({
          pointId: selectedZoneInstance.startPosition!.id,
          point: {
            coordinateX: updatedPoints.reduce((a, b) => a + b.point.coordinateX, 0) / updatedPoints.length,
            coordinateY: updatedPoints.reduce((a, b) => a + b.point.coordinateY, 0) / updatedPoints.length,
          } as Point,
          forcePosition: true,
        }),
      );
    }
  };

  const dragCreateFinishHandler = () => {
    if (selectedZoneInstance === null) {
      return;
    }
    const updatedPoints: { id: string; point: Point }[] = [];
    const initialPoint = selectedZoneInstance.points[0].point;
    if (zonePlannerMouseState === ZoneControllerMouseState.AddRedZoneLine) {
      if (
        selectedZoneInstance.points.length === 2 &&
        getDistanceBetweenTwoPoints(selectedZoneInstance.points[0].point, selectedZoneInstance.points[1].point) <
          MIN_WIDTH_THRESHOLD
      ) {
        updatedPoints.push({
          id: selectedZoneInstance.points[0].point.id,
          point: setPointInRoom(
            {
              coordinateX: initialPoint.coordinateX - 5 / zoomScale,
              coordinateY: initialPoint.coordinateY,
            } as Point,
            roomData,
          ),
        });
        updatedPoints.push({
          id: selectedZoneInstance.points[1].point.id,
          point: setPointInRoom(
            {
              coordinateX: initialPoint.coordinateX + 5 / zoomScale,
              coordinateY: initialPoint.coordinateY,
            } as Point,
            roomData,
          ),
        });
      }
    } else if (
      calculatePolygonArea(selectedZoneInstance.points.map((p) => p.point)) < MIN_AREA_THRESHOLD ||
      ([ZoneControllerMouseState.AddCleaningZone, ZoneControllerMouseState.AddTeachAndRepeatZone].includes(
        zonePlannerMouseState,
      ) &&
        [...selectedZoneInstance.points]
          .sort((a, b) => a.orderIndex - b.orderIndex)
          .some(
            (p, index) =>
              getDistanceBetweenTwoPoints(
                p.point,
                [...selectedZoneInstance.points].sort((a, b) => a.orderIndex - b.orderIndex)[
                  index === selectedZoneInstance.points.length - 1 ? 0 : index + 1
                ].point,
              ) <
              START_POINT_OFFSET * 2,
          ))
    ) {
      updatedPoints.push({
        id: selectedZoneInstance.points[0].point.id,
        point: setPointInRoom(
          {
            coordinateX: initialPoint.coordinateX - 5 / zoomScale,
            coordinateY: initialPoint.coordinateY + 5 / zoomScale,
          } as Point,
          roomData,
        ),
      });
      updatedPoints.push({
        id: selectedZoneInstance.points[1].point.id,
        point: setPointInRoom(
          {
            coordinateX: initialPoint.coordinateX + 5 / zoomScale,
            coordinateY: initialPoint.coordinateY + 5 / zoomScale,
          } as Point,
          roomData,
        ),
      });
      updatedPoints.push({
        id: selectedZoneInstance.points[2].point.id,
        point: setPointInRoom(
          {
            coordinateX: initialPoint.coordinateX + 5 / zoomScale,
            coordinateY: initialPoint.coordinateY - 5 / zoomScale,
          } as Point,
          roomData,
        ),
      });
      updatedPoints.push({
        id: selectedZoneInstance.points[3].point.id,
        point: setPointInRoom(
          {
            coordinateX: initialPoint.coordinateX - 5 / zoomScale,
            coordinateY: initialPoint.coordinateY - 5 / zoomScale,
          } as Point,
          roomData,
        ),
      });
    }
    let finalPointsList: IdTypePoint[] = [];
    updatedPoints.forEach((p) => {
      // dispatchUpdatePoint(p);
      finalPointsList.push({
        ...p.point,
        id: p.id,
        type: isTeachAndRepeatZone(selectedZoneInstance)
          ? ZonePointType.TeachAndRepeatZoneCorner
          : isCleaningZone(selectedZoneInstance)
            ? ZonePointType.CleaningZoneCorner
            : isRuleZone(selectedZoneInstance)
              ? ZonePointType.RuleZoneCorner
              : ZonePointType.RedZoneCorner,
      });
    });
    selectedZoneInstance.points // required to add dragged points to final points
      .filter((p) => !finalPointsList.some((fp) => fp.id === p.point.id))
      .forEach((p) => {
        finalPointsList.push(p.point);
      });

      if (isCleaningZone(selectedZoneInstance)) {
        const updatedStartPosition = {
          coordinateX: finalPointsList.reduce((a, b) => a + b.coordinateX, 0) / finalPointsList.length,
          coordinateY: finalPointsList.reduce((a, b) => a + b.coordinateY, 0) / finalPointsList.length,
        };
        const newZone = {
          ...selectedZoneInstance,
          startPosition: { ...selectedZoneInstance.startPosition, ...updatedStartPosition } as IdTypePoint,
          points: finalPointsList.map((p, index) => ({
            orderIndex: index,
            point: p,
          })),
        };

        if (newZone != null && isTeachAndRepeatZone(newZone)) {
          // timeout s.t. points on selectedZoneInstance are updated before generating path
          setTimeout(() => {
            if (newZone.startPosition != null) {
              generatePath(
                finalPointsList.map((p) => [p.coordinateX, p.coordinateY, 0]),
                [newZone.startPosition.coordinateX, newZone.startPosition.coordinateY, 0],
              );
            }
          }, 200);
        }

      if (
        isTeachAndRepeatZone(newZone) &&
        selectedTrPathInstance != null &&
        selectedTrPathInstance.zoneId == newZone.id
      ) {
        const startPositionAsPoint = {
          id: uuidv4(),
          type: ZonePointType.TeachAndRepeatZonePathPoint,
          coordinateX: newZone.startPosition.coordinateX,
          coordinateY: newZone.startPosition.coordinateY,
          coordinateTheta: newZone.startPosition.coordinateTheta,
          closeToObstacle: -1,
        } as PathPoint;
        const newTRPath = {
          ...selectedTrPathInstance,
          path:
            selectedTrPathInstance.path.length >= 2
              ? selectedTrPathInstance.path.map((p) => ({ ...p }))
              : [startPositionAsPoint, startPositionAsPoint],
        };
        dispatch(
          addUndoableAction({
            action: MapHistoryAction.Created,
            type: MapHistoryType.CleaningZone,
            changedData: newZone,
            additionalChangedData: newTRPath,
          }),
        );
        dispatch(
          postNewCleaningZone({
            cleaningZone: newZone,
            teachAndRepeatPath: newTRPath,
          }),
        );
      } else {
        dispatch(
          addUndoableAction({
            action: MapHistoryAction.Created,
            type: MapHistoryType.CleaningZone,
            changedData: newZone,
          }),
        );
        dispatch(postNewCleaningZone({ cleaningZone: newZone }));
      }
    } else if (isRuleZone(selectedZoneInstance)) {
      const newZone = {
        ...selectedZoneInstance,
        points: finalPointsList.map((p, index) => ({
          orderIndex: index,
          point: p,
        })),
      };
      dispatch(
        addUndoableAction({
          action: MapHistoryAction.Created,
          type: MapHistoryType.RuleZone,
          changedData: newZone,
        }),
      );
      dispatch(postNewRuleZone(newZone));
    } else {
      const newZone = {
        ...selectedZoneInstance,
        points: finalPointsList.map((p, index) => ({
          orderIndex: index,
          point: p,
        })),
      };
      dispatch(
        addUndoableAction({
          action: MapHistoryAction.Created,
          type: MapHistoryType.RedZone,
          changedData: newZone,
        }),
      );
      dispatch(postNewRedZone(newZone));
    }

    dispatch(setZonePlannerMouseState(ZoneControllerMouseState.ShowDetails));
  };

  const mouseUpEndActionHandler = () => {
    if (isAddingState(zonePlannerMouseState)) {
      dragCreateFinishHandler();
    } else if (zonePlannerMouseState === ZoneControllerMouseState.AddGate) {
      if (!navTasks.data?.ToStart.alongWaypoints || !navTasks.data.ZoneCleaning.approachAlongWaypoints) {
        dispatch(toggleWaypointsEnabledAutomatically());
        const newNavTask = {
          ToStart: {
            alongWaypoints: true,
          },
          ZoneCleaning: {
            approachAlongWaypoints: true,
          },
        };
        dispatch(patchNavTasks(newNavTask));
      }
      dispatch(setZonePlannerMouseState(ZoneControllerMouseState.Default));
      dispatch(postNewGate());
    } else if (gatesData.selectedId != null && zonePlannerMouseState !== ZoneControllerMouseState.DeleteZone) {
      dispatch(patchGateById({ id: gatesData.selectedId, data: {} }));
    } else if (
      (zonePlannerMouseState === ZoneControllerMouseState.DragZone ||
        selectedZone.selectedPointId != null ||
        selectedTrPath.selectedTeachAndRepeatPathPointId != null) &&
      selectedZoneInstance != null
    ) {
      if (isCleaningZone(selectedZoneInstance)) {
        let path = null;
        if (
          selectedTrPathInstance != null &&
          selectedZoneInstance.startPosition != null &&
          selectedTrPathInstance.path != null &&
          selectedTrPathInstance.path.length > 0
        ) {
          path = selectedTrPathInstance.path.map((pt) => ({ ...pt }));
          if (selectedTrPath.selectedTeachAndRepeatPathPointId === selectedTrPathInstance.path[0].id) {
            // we moved the starting position of the trp zone which is the first point of the path
            path[0] = {
              coordinateX: selectedZoneInstance.startPosition.coordinateX,
              coordinateY: selectedZoneInstance.startPosition.coordinateY,
              coordinateTheta: selectedZoneInstance.startPosition.coordinateTheta,
              closeToObstacle: -1, // signifies the proximity to objects label to be UNKNOWN
              id: selectedZoneInstance.startPosition.id,
              type: ZonePointType.TeachAndRepeatZonePathPoint,
            };
          }
        }
        // to avoid patch errors after copy
        if (
          cleaningZonesData.rest.postStatus === HttpFetchStatus.Idle ||
          cleaningZonesData.rest.postStatus === HttpFetchStatus.Success
        ) {
          dispatch(
            patchCleaningZone({
              id: selectedZoneInstance.id,
              data: {
                cleaningZone: {
                  points: selectedZoneInstance.points,
                  startPosition: selectedZoneInstance.startPosition,
                },
                ...(path != null && { teachAndRepeatPath: { path: path } }),
              },
            }),
          );
        }
      } else if (isRuleZone(selectedZoneInstance)) {
        dispatch(
          patchRuleZone({
            id: selectedZoneInstance.id,
            data: { id: selectedZoneInstance.id, points: selectedZoneInstance.points },
          }),
        );
      } else {
        // to avoid patch errors after copy
        if (
          redZonesData.rest.postStatus === HttpFetchStatus.Idle ||
          redZonesData.rest.postStatus === HttpFetchStatus.Success
        ) {
          dispatch(
            patchRedZone({
              id: selectedZoneInstance.id,
              data: { id: selectedZoneInstance.id, points: selectedZoneInstance.points },
            }),
          );
        }
      }
    } else if (
      [ZoneControllerMouseState.AddLandmarkPoint, ZoneControllerMouseState.AddEvacuationPlace].includes(
        zonePlannerMouseState,
      ) &&
      selectedLandmark != null
    ) {
      dispatch(postNewLandmark(selectedLandmark));
    } else if (zonePlannerMouseState === ZoneControllerMouseState.DragZone && selectedLandmark != null) {
      dispatch(patchLandmark({ id: selectedLandmark.id, data: { landmarkPoint: selectedLandmark.landmarkPoint } }));
    } else if (zonePlannerMouseState === ZoneControllerMouseState.DragZone && selectedWaypointNode != null) {
      dispatch(
        upsertWaypointSet({
          room: roomData.id,
          edges: waypointsData.data.edges.filter((e) => e.room === roomData.id),
          nodes: waypointsData.data.nodes.filter((n) => n.room === roomData.id),
        }),
      );
    }

    if (chargingStationDragData.isDragging === true) {
      if (
        redZones.some(
          (z) =>
            validateRedZoneStationOverlap(z, {
              ...roomData.primaryStation,
              hasChargingCapability: true,
              stationId: "chargingStation",
            }).hasOverlaps,
        ) &&
        roomData.primaryStation != null
      ) {
        dispatch(
          patchStationDrag({
            ...getDefaultChargingStationMove(roomData.primaryStation),
            error: "charging-station-overlaps-redzone",
            isSelected: true,
          }),
        );
      } else {
        dispatch(patchStationDrag({ isDragging: false }));
        dispatch(
          sendRobotWebsocketMessage({
            type: MessageType.MoveStation,
            message: {
              ...roomData.primaryStation,
              roomId: roomData.id,
            },
          }),
        );
      }
    }

    if (selectedZone.id == null) {
      return;
    }
    dispatch(setSelectedZone({ id: selectedZone.id }));
  };

  const svgMouseUpHandler = (event: React.PointerEvent) => {
    event.preventDefault();
    mouseUpEndActionHandler();
  };

  const svgTouchEndHandler = (_event: React.PointerEvent) => {
    document.body.style.overscrollBehavior = "";
    mouseUpEndActionHandler();
  };

  const dragZone = (event: React.PointerEvent) => {
    const svgPoint = getSvgPointFromMouseClick(event, svgRef, zoomScale);
    const rosPoint = getRosPointFromSvgCoordinates(svgPoint!.x, svgPoint!.y, roomData);
    if (rosPoint == null) {
      return;
    }
    dispatch(
      updateSelectedZoneCoordinatesOnDrag({
        initialDragPoint: selectedZone.prevDragPoint == null ? rosPoint : selectedZone.prevDragPoint,
        newDragPoint: rosPoint,
        roomData,
      }),
    );
  };

  const updateWayPointMousePositionHandler = (event: React.PointerEvent) => {
    const svgPoint = getSvgPointFromMouseClick(event, svgRef, zoomScale);
    const rosPoint = getRosPointFromSvgCoordinates(svgPoint!.x, svgPoint!.y, roomData);
    if (rosPoint == null) {
      return;
    }
    dispatch(updateWayPointMousePosition(rosPoint));
  };

  const dragWayPointHandler = (event: React.PointerEvent) => {
    const svgPoint = getSvgPointFromMouseClick(event, svgRef, zoomScale);
    const newPoint = getRosPointFromSvgCoordinates(svgPoint!.x, svgPoint!.y, roomData);
    if (newPoint !== null) {
      dispatch(
        dragWayPoint(
          setPointInRoom(
            {
              coordinateX: newPoint.coordinateX,
              coordinateY: newPoint.coordinateY,
            } as Point,
            roomData,
          ),
        ),
      );
    }
  };

  const dragLandmarkHandler = (event: React.PointerEvent) => {
    const svgPoint = getSvgPointFromMouseClick(event, svgRef, zoomScale);
    const newPoint = getRosPointFromSvgCoordinates(svgPoint!.x, svgPoint!.y, roomData);
    if (newPoint !== null) {
      dispatch(
        dragLandmark(
          setPointInRoom(
            {
              coordinateX: newPoint.coordinateX,
              coordinateY: newPoint.coordinateY,
            } as Point,
            roomData,
          ),
        ),
      );
    }
  };

  const dragChargingStationHandler = (event: React.PointerEvent) => {
    const svgPoint = getSvgPointFromMouseClick(event, svgRef, zoomScale);
    const newPoint = getRosPointFromSvgCoordinates(svgPoint!.x, svgPoint!.y, roomData);
    if (newPoint != null) {
      dispatch(patchStationDrag({ position: { ...roomData.primaryStation, ...newPoint }, unsavedChanges: true }));
    }
  };

  const svgMouseTouchMoveHandler = (event: React.PointerEvent) => {
    if (missionView != null) return;
    if (zonePlannerMouseState === ZoneControllerMouseState.DragZone && selectedZoneInstance !== null) {
      dragZone(event);
    } else if (
      isAddingState(zonePlannerMouseState) &&
      selectedZoneInstance !== null &&
      selectedZoneInstance?.points.length > 0
    ) {
      dragCreateMoveHandler(event);
    } else if (
      zonePlannerMouseState === ZoneControllerMouseState.AddGate ||
      (zonePlannerMouseState === ZoneControllerMouseState.DragZone && gatesData.selectedId !== null)
    ) {
      dragGateHandler(event);
    } else if (zonePlannerMouseState === ZoneControllerMouseState.AddWayPoint && selectedWaypointNode != null) {
      updateWayPointMousePositionHandler(event);
    } else if (zonePlannerMouseState === ZoneControllerMouseState.DragZone && selectedWaypointNode != null) {
      dragWayPointHandler(event);
    } else if (zonePlannerMouseState === ZoneControllerMouseState.DragZone && landmarksData.selectedId !== null) {
      dragLandmarkHandler(event);
    } else if (chargingStationDragData.isDragging === true && isAdvancedModeEnabled) {
      dragChargingStationHandler(event);
    }

    const start = draggingStartRef.current;
    if (!start) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;

    const distance = Math.sqrt(dx * dx + dy * dy);

    if (!isDraggingRef.current && distance > 5 && (event.pointerType !== "mouse" || event.buttons === 1)) {
      isDraggingRef.current = true;

      if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
    }

    if (!isDraggingRef.current) return;
    // update selected point coordinates (effectively drag the point)
    const svgPoint = getSvgPointFromMouseClick(event, svgRef, zoomScale);
    let updatedPoint = getRosPointFromSvgCoordinates(svgPoint!.x, svgPoint!.y, roomData);
    if (updatedPoint) {
      updatedPoint = setPointInRoom(updatedPoint, roomData);
    }
    if (updatedPoint != null) {
      if (selectedZone.selectedPointId != null) {
        dispatch(
          updateSelectedZonePointCoordinates({
            pointId: selectedZone.selectedPointId,
            point: updatedPoint,
            saveHistory: true,
          }),
        );
      } else if (selectedTrPath.selectedTeachAndRepeatPathPointId != null) {
        dispatch(
          updateSelectedTeachAndRepeatPathPointCoordinates({
            pointId: selectedTrPath.selectedTeachAndRepeatPathPointId,
            point: updatedPoint,
          }),
        );
      }
    }
  };

  let cursorStyle = "";
  if (zonePlannerMouseState === ZoneControllerMouseState.AddCleaningZone) {
    cursorStyle = newCleaningZoneCursorSvg;
  }
  if (zonePlannerMouseState === ZoneControllerMouseState.AddTeachAndRepeatZone) {
    cursorStyle = newTeachAndRepeatZoneCursorSvg;
  } else if (
    zonePlannerMouseState === ZoneControllerMouseState.AddRedZone ||
    zonePlannerMouseState === ZoneControllerMouseState.AddRedZoneLine
  ) {
    cursorStyle = newRedZoneCursorSvg;
  } else if (zonePlannerMouseState === ZoneControllerMouseState.AddRuleZone) {
    cursorStyle = newRuleZoneCursorSvg;
  } else if (zonePlannerMouseState === ZoneControllerMouseState.AddWayPoint) {
    cursorStyle = newWayPointCursorSvg;
  } else if (zonePlannerMouseState === ZoneControllerMouseState.AddGate) {
    cursorStyle = newGateCursorSvg;
  } else if (
    ((zonePlannerMouseState === ZoneControllerMouseState.DragZone ||
      zonePlannerMouseState === ZoneControllerMouseState.CopyZone) &&
      (selectedZoneInstance != null || selectedWaypointNode != null)) ||
    chargingStationDragData.isDragging
  ) {
    cursorStyle = draggingCursor;
  } else if (zonePlannerMouseState === ZoneControllerMouseState.DragZone) {
    cursorStyle = grabCursor;
  } else if (zonePlannerMouseState === ZoneControllerMouseState.CopyZone) {
    cursorStyle = copyCursor;
  } else if (
    zonePlannerMouseState === ZoneControllerMouseState.DeleteZone ||
    zonePlannerMouseState === ZoneControllerMouseState.DeletePoint
  ) {
    cursorStyle = deleteCursorSvg;
  } else if (zonePlannerMouseState === ZoneControllerMouseState.AddLandmarkPoint) {
    cursorStyle = newLandmarkCursorSvg;
  } else if (zonePlannerMouseState === ZoneControllerMouseState.AddEvacuationPlace) {
    cursorStyle = newEvacuationPlaceCursorSvg;
  }

  // Move selected zone to the end of the list so its rendered on top
  if (selectedZoneInstance != null) {
    allZoneIds.push(allZoneIds.splice(allZoneIds.indexOf(selectedZoneInstance.id), 1)[0]);
  }

  const gridSize = mapScaleToValue(zoomScale, roomData.mapInformation.resolution, distanceUnit).gridSize;

  let roomLimitZonePolygonPoints: string | null = null;
  if (roomLimitZone != null) {
    roomLimitZonePolygonPoints = zonePointsToSvgString(roomLimitZone.points, roomData);
  }

  let filteredZoneIds: string[] = [];
  if (
    zonePlannerModeState === ZonePlannerModeState.EditAutoGeneratedTeachAndRepeatPath &&
    currentTeaching.zoneId != null
  ) {
    filteredZoneIds = allZoneIds.filter((zoneId) => zoneId === currentTeaching.zoneId);
  } else {
    filteredZoneIds = allZoneIds
      .filter(
        (zoneId) =>
          cleaningZones.some((cz) => cz.id === zoneId) ||
          redZones.some((rz) => rz.id === zoneId) ||
          ruleZones.some((rz) => rz.id === zoneId),
      )
      .filter((zoneId) => scheduleLandmarkSelectionProps == null)
      .filter(
        (zoneId) =>
          (!hiddenZoneIds.includes(zoneId) || missionView != null) &&
          ((zonePlannerModeState === ZonePlannerModeState.Default &&
            !redZones.some((rz) => rz.id === zoneId && (rz.evacuation === true || missionView != null)) &&
            !ruleZones.some((rz) => rz.id === zoneId)) ||
            (zonePlannerModeState === ZonePlannerModeState.EvacuationMode &&
              !cleaningZones.some((cz) => cz.id === zoneId)) ||
            (zonePlannerModeState === ZonePlannerModeState.MapRules &&
              !redZones.some((rz) => rz.id === zoneId && (rz.evacuation === true || missionView != null)) &&
              !cleaningZones.some((cz) => cz.id === zoneId))),
      );
  }

  return (
    <React.Fragment>
      <svg
        viewBox={`0 0 ${roomData.mapInformation.width} ${roomData.mapInformation.height}`}
        ref={svgRef}
        onPointerDown={(e) => {
          isDraggingRef.current = false;
          pointerIdRef.current = e.pointerId;

          draggingStartRef.current = {
            x: e.clientX,
            y: e.clientY,
          };

          svgMouseTouchDownHandler(e);
        }}
        onPointerMove={svgMouseTouchMoveHandler}
        onPointerUp={(e) => {
          if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
          }
          isDraggingRef.current = false;
          draggingStartRef.current = null;
          svgTouchEndHandler(e as any);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
        }}
        className={classes.overlayDrawingSvg}
        style={{
          cursor: cursorStyle,
          height: roomData.mapInformation.height,
          width: roomData.mapInformation.width,
          touchAction: "none",
        }}
      >
        {/* Evacuation-zone stripes pattern — shared by all ZonePlannerMapZone children */}
        <defs>
          <pattern id="stripes" patternUnits="userSpaceOnUse" width="10" height="10">
            <rect width="2" height="10" fill="red" />
            <rect width="8" height="10" x="2" y="0" fill="darkCoral" />
          </pattern>
        </defs>

        {currentRobotState?.position != null && currentRobotState?.status?.roomId === roomData.id && (
          <KRobotSvg
            coordinateX={
              getMapCoordinatesFromPoint(
                { x: currentRobotState.position.x, y: currentRobotState.position.y },
                {
                  height: roomData.mapInformation.height,
                  resolution: roomData.mapInformation.resolution,
                  xPosition: roomData.mapInformation.xPosition,
                  yPosition: roomData.mapInformation.yPosition,
                },
              ).x
            }
            coordinateY={
              getMapCoordinatesFromPoint(
                { x: currentRobotState.position.x, y: currentRobotState.position.y },
                {
                  height: roomData.mapInformation.height,
                  resolution: roomData.mapInformation.resolution,
                  xPosition: roomData.mapInformation.xPosition,
                  yPosition: roomData.mapInformation.yPosition,
                },
              ).y
            }
            coordinateTheta={currentRobotState.position.theta}
            robotRadius={robotRadius}
            model={robotVersion ?? RobotVersion.K900}
          />
        )}

        {roomLimitZonePolygonPoints != null && missionView == null && (
          <React.Fragment>
            <mask id="mask">
              <rect width="100%" height="100%" fill="white" />
              <polygon points={roomLimitZonePolygonPoints} fill="black" />
            </mask>
            <rect width="100%" height="100%" fill="black" fillOpacity="0.5" mask="url(#mask)" />
          </React.Fragment>
        )}

        {filteredZoneIds.map((zoneId) => (
          <ZonePlannerMapZone
            key={zoneId}
            zoneId={zoneId}
            roomData={roomData}
            scale={zoomScale}
            mouseState={zonePlannerMouseState}
            deletable={!gates.some((g) => g.redzone === zoneId) && missionView == null}
            isSelected={zoneId === selectedZoneInstance?.id}
            isReadOnly={isReadOnly}
            notSelectable={missionView != null}
            hoverZoneId={hoverZoneId ?? missionView?.hoveredIds.find((id) => id === zoneId) ?? null}
            hidePath={hidePaths}
          />
        ))}

        {isWayPointMapViewable &&
          (zonePlannerModeState === ZonePlannerModeState.Default ||
            zonePlannerModeState === ZonePlannerModeState.MapRules ||
            zonePlannerModeState === ZonePlannerModeState.EvacuationMode) &&
          missionView == null && (
            <WaypointsMap scale={zoomScale} roomData={roomData} mouseState={zonePlannerMouseState} />
          )}

        {(zonePlannerModeState === ZonePlannerModeState.EvacuationMode ||
          zonePlannerModeState === ZonePlannerModeState.Default) && (
          <Landmarks
            scale={zoomScale}
            roomData={roomData}
            mouseState={zonePlannerMouseState}
            scheduleLandmarkSelectionProps={scheduleLandmarkSelectionProps}
            isMissionView={missionView != null}
          />
        )}

        {zonePlannerModeState !== ZonePlannerModeState.EditAutoGeneratedTeachAndRepeatPath &&
          scheduleLandmarkSelectionProps == null &&
          missionView == null &&
          gates
            .filter((g) => !hiddenGateIds.includes(g.id))
            .map((g) => (
              <GatePosts
                gate={g}
                scale={zoomScale}
                key={g.id}
                roomData={roomData}
                isHovering={g.id === hoverGateId || g.id === gatesData.selectedId}
                mouseState={zonePlannerMouseState}
                notSelectable={zonePlannerModeState !== ZonePlannerModeState.MapRules}
              />
            ))}

        {zonePlannerModeState !== ZonePlannerModeState.EditAutoGeneratedTeachAndRepeatPath && missionView == null && (
          <React.Fragment>
            <ChargingStation
              zoomScale={zoomScale}
              roomData={roomData}
              station={{ ...chargingStationDragData, position: roomData.primaryStation }}
              mouseState={zonePlannerMouseState}
            />
            {roomData.secondaryStation != null && (
              <ChargingStation
                zoomScale={zoomScale}
                roomData={roomData}
                station={{
                  position: { ...roomData.secondaryStation },
                  error: null,
                  isDragging: false,
                  isSelected: false,
                  unsavedChanges: false,
                  saveStatus: MoveStationSaveStatus.Idle,
                }}
                mouseState={zonePlannerMouseState}
              />
            )}
          </React.Fragment>
        )}

        {isGridVisible && (
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" className={classes.gridStyle} data-testid="map-planner-grid-overlay">
            <defs>
              <pattern
                id="grid"
                width={gridSize}
                height={gridSize}
                patternUnits="userSpaceOnUse"
                x={getSvgCoordinatesFromRosPoint(mapOriginPoint, roomData)!.x}
                y={getSvgCoordinatesFromRosPoint(mapOriginPoint, roomData)!.y}
              >
                <path d={`M ${gridSize} 0 L 0 0 0 ${gridSize}`} fill="none" stroke="gray" strokeWidth={1 / zoomScale} />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        )}
      </svg>

      <TeachAndRepeatZoneGeneratingPathState />
    </React.Fragment>
  );
};

export default ZonePlannerMapZones;
